// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { BillingFrequency } from '@/constants/billing-frequency';
import { ChargeSchedule } from '@/constants/charge-schedule';
import { InvoiceMode } from '@/constants/invoice-mode';
import { PricingMode } from '@/constants/pricing-mode';
import { Currency } from '@/currency/api/types';
import { serializeCurrencyForAPINullable } from '@/currency/utilities/serialize';
import { itemDetailSchema } from '@/items/api/types';
import { z } from 'zod';

export interface UpdateItemPricingParameters {
  pricingMode: PricingMode | null;
  unitPrice: Currency | null;
  fixedPrice: Currency | null;
  chargeSchedule: ChargeSchedule | null;
  chargeFrequency: BillingFrequency | null;
  invoiceMode: InvoiceMode | null;
  invoiceFrequency: BillingFrequency | null;
}

const updateItemPricingResponseSchema = z
  .object({ data: itemDetailSchema, metadata: responseMetadataSchema })
  .transform(({ data, ...response }) => ({ ...response, item: data }));

export type UpdateItemPricingResponse = z.infer<
  typeof updateItemPricingResponseSchema
>;

export async function updateItemPricing(
  itemKey: string,
  parameters: UpdateItemPricingParameters
): Promise<UpdateItemPricingResponse> {
  const url = `/v1/catalog/item/${itemKey}/pricing`;
  const response = await api.patch(url, {
    ...parameters,
    unitPrice: serializeCurrencyForAPINullable(parameters.unitPrice),
    fixedPrice: serializeCurrencyForAPINullable(parameters.fixedPrice),
  });
  return parseResponse(updateItemPricingResponseSchema, response.data, {
    schemaName: 'UpdateItemPricing',
    endpoint: url,
  });
}
